import { useEffect, useRef, useCallback } from 'react';
import { trpc } from '@/lib/trpc';
import { DEFAULT_AUTO_LOGOUT_MINUTES, hasIdleTimeoutElapsed, resolveAutoLogoutMinutes } from '../../../shared/idle-timeout';
import { IDLE_TIMEOUT_QUERY_POLICY } from '@/lib/authQueryPolicy';

export const INACTIVITY_ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'touchmove',
  'pointerdown',
  'pointermove',
  'focus',
] as const;

export function useSimpleInactivity(onTimeout: () => void, tenantSlug?: string, enabled = true) {
  const lastActivityRef = useRef<number>(Date.now());
  const onTimeoutRef = useRef(onTimeout);
  const idleTimeoutMsRef = useRef(DEFAULT_AUTO_LOGOUT_MINUTES * 60 * 1000);
  const timeoutTriggeredRef = useRef(false);

  // Update the ref when onTimeout changes
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  // Fetch the idle timeout setting from the server
  const { data: idleTimeoutData } = trpc.sessions.getIdleTimeout.useQuery(undefined, {
    ...IDLE_TIMEOUT_QUERY_POLICY,
    enabled: enabled && !tenantSlug,
  });
  const { data: tenantIdleTimeoutData } = trpc.saas.getTenantAdminSettings.useQuery(
    { slug: tenantSlug ?? "__legacy__" },
    { ...IDLE_TIMEOUT_QUERY_POLICY, enabled: enabled && !!tenantSlug },
  );

  const idleTimeoutMinutes = resolveAutoLogoutMinutes(
    (tenantSlug ? tenantIdleTimeoutData?.idleTimeoutMinutes : idleTimeoutData?.idleTimeoutMinutes) ?? DEFAULT_AUTO_LOGOUT_MINUTES,
  );
  const idleTimeoutMs = idleTimeoutMinutes * 60 * 1000;

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    timeoutTriggeredRef.current = false;
  }, []);

  useEffect(() => {
    idleTimeoutMsRef.current = idleTimeoutMs;
  }, [idleTimeoutMs]);

  useEffect(() => {
    if (!enabled) return;

    // Capture interactions before React tab handlers. A one-shot timeout could
    // fire during a tab click if an old timer was queued; elapsed-time checks
    // below avoid that false logout path.
    const events = INACTIVITY_ACTIVITY_EVENTS;

    // Reset timer on any activity
    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });
    window.addEventListener("tracklabs:activity", handleActivity);

    resetTimer();

    const interval = window.setInterval(() => {
      if (!timeoutTriggeredRef.current && hasIdleTimeoutElapsed(lastActivityRef.current, Date.now(), idleTimeoutMsRef.current)) {
        timeoutTriggeredRef.current = true;
        onTimeoutRef.current();
      }
    }, 5000);

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      window.removeEventListener("tracklabs:activity", handleActivity);
      window.clearInterval(interval);
    };
  }, [enabled, resetTimer]);
}
